import type { AddStickPostOptions } from "@/components/AddStickyPostsButton";
import {
  addCollaboratorPost,
  deleteStickyPost,
  addPostsToFeed,
  addStickyPost,
  applyCollaboratorDraftLogic,
  backfillFeed,
  editFeed,
  hidePost,
  pinCollaboratorPost,
  publishFeed,
  removeCollaboratorPost,
  resolvePostAtUri,
  restoreCollaboratorPost,
  saveCollaboratorDraftLogic,
  unHidePost,
  unpinCollaboratorPost,
  triggerCollaboratorBackfill,
} from "@/lib/api";

/**
 * The same actions, through whichever door the viewer is allowed through.
 *
 * 🔑 THE SERVER HAS TWO DOORS AND THAT IS DELIBERATE. The owner's endpoints are guarded by
 * `authorize_object`, which is pure ownership and is used by `/publish_algo`, `/delete_algo`
 * and dozens more; teaching it about capabilities would widen every one of those at once.
 * So collaborators get a parallel set of routes under `/api/v1/collab` that check one
 * capability each. That split belongs in the API layer, not in the UI — which is what this
 * module is for.
 *
 * The point is that there is ONE set of controls. A collaborator uses the feed's own sticky
 * card, its own backfill button and its own add-post dialog; only the request underneath
 * differs. The alternative — a separate "what you can do" panel — was a second UI for the
 * same jobs, which is how someone ends up with two "add a post" boxes on one page and no
 * idea which is real.
 */

/** Add posts, keeping their own timestamp or stamping them to the top. */
export async function insertPosts(input: {
  feedId: number;
  postUrls: string[];
  toTop: boolean;
  asCollaborator: boolean;
}) {
  if (!input.asCollaborator) {
    return addPostsToFeed({
      algo_id: input.feedId,
      post_urls: input.postUrls,
      current_time: input.toTop,
    });
  }
  // The collaborator route takes one post at a time. Sequential rather than parallel so a
  // failure half way leaves a comprehensible state: everything before it landed.
  for (const postUrl of input.postUrls) {
    await addCollaboratorPost({
      feedId: input.feedId,
      postUrl,
      mode: input.toTop ? "promoted" : "organic",
    });
  }
}

/**
 * Take posts out of the feed — spam, junk, a rumour that should not travel.
 *
 * Both doors end in the same place, `hidden_status`, which the serve path filters on, so a
 * removed post stays out however many times it re-matches. The collaborator door also
 * records who removed it, so the owner can see it in the feed's history and undo it.
 */
export async function removePosts(input: {
  feedId: number;
  postUrls: string[];
  asCollaborator: boolean;
}) {
  if (!input.asCollaborator) {
    await Promise.all(
      input.postUrls.map((url) =>
        hidePost({ at_uri: url, algo_id: input.feedId }),
      ),
    );
    return;
  }
  // One at a time, like `insertPosts`: a failure half way leaves everything before it removed.
  for (const url of input.postUrls) {
    await removeCollaboratorPost({
      feedId: input.feedId,
      postUri: await resolvePostAtUri(url),
    });
  }
}

/**
 * Put a removed post back.
 *
 * 🔑 A COLLABORATOR CAN ONLY UNDO A COLLABORATOR REMOVAL. Restore reverses the removal
 * record the collaborator door writes; a post the owner hid has no such record and the
 * server answers 404 "That post is not currently removed". That is the right answer —
 * the owner's decision is theirs to reverse.
 */
export async function restorePost(input: {
  feedId: number;
  postUri: string;
  asCollaborator: boolean;
}) {
  if (!input.asCollaborator) {
    return unHidePost({ at_uri: input.postUri, algo_id: input.feedId });
  }
  return restoreCollaboratorPost({
    feedId: input.feedId,
    postUri: input.postUri,
  });
}

/** A pin left up past its moment is worse than no pin, so the collaborator route demands one. */
const COLLABORATOR_PIN_MINUTES = 120;

export async function addSticky(input: {
  feedId: number;
  options: AddStickPostOptions;
  asCollaborator: boolean;
}) {
  if (!input.asCollaborator) {
    return addStickyPost({
      algo_id: input.feedId,
      is_active: true,
      ...input.options,
    });
  }
  // The collaborator route pins and expires, nothing else — `manage_sticky_posts` does not
  // carry the power to inject a post permanently. The dialog is told to offer only pinning
  // (see `pinnedOnly`), so reaching here with anything else would be a bug, not a choice.
  const expiresInMinutes = input.options.expiration
    ? Math.max(
        1,
        Math.round(
          (input.options.expiration.getTime() - Date.now()) / 60_000,
        ),
      )
    : COLLABORATOR_PIN_MINUTES;
  return pinCollaboratorPost({
    feedId: input.feedId,
    postUrl: input.options.post_url,
    expiresInMinutes,
  });
}

/**
 * Remove a pin.
 *
 * `manage_sticky_posts` says "pin and unpin", and the collaborator route for the second
 * half existed from the start — it just had no caller, so the capability delivered half of
 * what its own label promised: pin a post, then be refused when you try to take it down.
 */
export async function removeSticky(input: {
  feedId: number;
  stickyPostId: number;
  assocId?: number;
  stickyType?: string;
  asCollaborator: boolean;
}) {
  if (!input.asCollaborator) {
    return deleteStickyPost({
      algo_id: input.feedId,
      sticky_post_id: input.stickyPostId,
      assoc_id: input.assocId,
      sticky_type: input.stickyType,
    });
  }
  return unpinCollaboratorPost({
    feedId: input.feedId,
    stickyPostId: input.stickyPostId,
    assocId: input.assocId,
    stickyType: input.stickyType,
  });
}

/**
 * Save what the feed should match.
 *
 * 🔑 THE TWO DOORS DIVERGE IN WHAT THEY WILL ACCEPT, not just in who may knock. The owner's
 * save posts the whole edit form — which carries the feed's IDENTITY as well as its rules:
 * `record_name`, the avatar file, the display name. The collaborator route takes four
 * fields and nothing else, so a collaborator cannot rename or re-skin a feed as a side
 * effect of proposing a rule change. Pulling only those four off the form is what enforces
 * that on this side.
 *
 * And it saves a DRAFT. `edit_feed_logic` proposes; making it live is `publishFeedLogic`,
 * which a collaborator needs a separate grant for, because rules are the one thing that
 * changes the feed for every reader at once.
 */
export async function saveFeedLogic(input: {
  feedId: number;
  form: FormData;
  asCollaborator: boolean;
}) {
  if (!input.asCollaborator) {
    return editFeed(input.feedId, input.form);
  }
  const read = (key: string) => {
    const raw = input.form.get(key);
    return typeof raw === "string" && raw ? raw : undefined;
  };
  const parse = (key: string) => {
    const raw = read(key);
    if (!raw) return undefined;
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  };
  const manifest = parse("algorithm_manifest");
  if (!manifest) {
    throw new Error("Could not read the feed's rules from the editor.");
  }
  return saveCollaboratorDraftLogic({
    feedId: input.feedId,
    manifest,
    order: read("order"),
    customSortSettings: parse("custom_sort_settings"),
    metadata: parse("metadata"),
    // FeedForm's own keys. Omitted, the server keeps what is live rather than clearing it.
    emit: read("emit"),
    onEmitNull: read("onEmitNull"),
  });
}

/**
 * Make the saved draft what the feed matches.
 *
 * The owner publishes; a collaborator applies, which needs `apply_feed_logic` and refuses a
 * feed that has never been published. Gate the button on `canPublishFeedLogic` so a
 * collaborator without the grant is never offered a Publish that can only 403.
 */
export async function publishFeedLogic(input: {
  feedId: number;
  asCollaborator: boolean;
}) {
  if (!input.asCollaborator) {
    return publishFeed(input.feedId);
  }
  await applyCollaboratorDraftLogic(input.feedId);
}

/** Whether to offer Publish at all. `can` is true for the owner. */
export function canPublishFeedLogic(access: {
  isOwner: boolean;
  can: (capability: "apply_feed_logic") => boolean;
}) {
  return access.isOwner || access.can("apply_feed_logic");
}

/**
 * Re-scan recent history.
 *
 * The two endpoints answer differently — the owner's returns an optional `warning`, the
 * collaborator's returns `enqueued` plus a `reason` when it declined — so this normalises
 * them into the one thing the caller wants to say out loud.
 */
export async function runBackfill(input: {
  feedId: number;
  asCollaborator: boolean;
}): Promise<{ started: boolean; note?: string }> {
  if (!input.asCollaborator) {
    const status = await backfillFeed(input.feedId);
    return { started: true, note: status.warning ?? undefined };
  }
  const result = await triggerCollaboratorBackfill(input.feedId, 24);
  return {
    started: result.enqueued,
    note: result.enqueued
      ? undefined
      : result.reason === "in_progress"
        ? "One is already running on this feed."
        : result.reason,
  };
}
